import React, { useState, useEffect } from "react";
import { Routes, Route, Link, useLocation, Navigate } from "react-router-dom";
import UserAdmin from "./UserAdmin";
import ChatStats from "./ChatStats";
import MonitoringDashboard from "./MonitoringDashboard";
import BotList from "./BotList";
import BotAdmin from "./BotAdmin";
import TwitchApps from "./TwitchApps";
import TwitchChat from "./TwitchChat";
import EmailAdmin from "./EmailAdmin";
import { Icon } from "./Icon";

interface AdminProps {
  authToken: string;
}

const Admin: React.FC<AdminProps> = ({ authToken }) => {
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(false);

  // Get active subtab from URL
  const pathSegments = location.pathname.split("/");
  let activeSubTab = pathSegments[3] || "users";

  // Handle sub-routes for tabs that have child routes
  if (pathSegments[3] === "monitoring" && pathSegments[4]) {
    // For routes like /dashboard/admin/monitoring/streams
    activeSubTab = "monitoring";
  } else if (pathSegments[3] === "memories" && pathSegments[4]) {
    // For routes like /dashboard/admin/memories/...
    activeSubTab = "memories";
  }

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 860);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Sub-navigation style
  const subNavStyle = {
    display: "flex",
    gap: "20px",
    marginBottom: "24px",
    borderBottom: "1px solid #4a5568",
    paddingBottom: "12px",
  };

  const subNavLinkStyle = (isActive: boolean) => ({
    color: isActive ? "#9146FF" : "#cbd5e0",
    textDecoration: "none",
    fontSize: "16px",
    fontWeight: isActive ? "600" : "400",
    paddingBottom: "12px",
    borderBottom: isActive ? "2px solid #9146FF" : "2px solid transparent",
    transition: "all 0.2s",
  });

  return (
    <div
      style={{
        maxWidth: "1200px",
        margin: "0 auto",
        padding: "0 20px",
        width: "100%",
      }}
    >
      <h2 style={{ fontSize: "24px", marginBottom: "24px", color: "#e2e8f0" }}>
        <Icon
          name="shield-check"
          size={22}
          style={{ verticalAlign: "-3px", marginRight: "8px" }}
        />
        Admin Panel
      </h2>

      {/* Sub-navigation */}
      <div style={subNavStyle}>
        <Link
          to="/dashboard/admin/users"
          style={subNavLinkStyle(activeSubTab === "users")}
        >
          User Admin
        </Link>
        <Link
          to="/dashboard/admin/chat-stats"
          style={subNavLinkStyle(activeSubTab === "chat-stats")}
        >
          Chat Stats
        </Link>
        <Link
          to="/dashboard/admin/monitoring"
          style={subNavLinkStyle(activeSubTab === "monitoring")}
        >
          Monitoring
        </Link>
        <Link
          to="/dashboard/admin/bot-list"
          style={subNavLinkStyle(activeSubTab === "bot-list")}
        >
          Bot List
        </Link>
        <Link
          to="/dashboard/admin/bots"
          style={subNavLinkStyle(activeSubTab === "bots")}
        >
          Bot Credentials
        </Link>
        <Link
          to="/dashboard/admin/twitch-apps"
          style={subNavLinkStyle(activeSubTab === "twitch-apps")}
        >
          Twitch Apps
        </Link>
        <Link
          to="/dashboard/admin/twitch-chat"
          style={subNavLinkStyle(activeSubTab === "twitch-chat")}
        >
          Twitch Chat
        </Link>
        <Link
          to="/dashboard/admin/email"
          style={subNavLinkStyle(activeSubTab === "email")}
        >
          Email
        </Link>
      </div>

      {/* Sub-routes */}
      <Routes>
        <Route path="/" element={<Navigate to="users" replace />} />
        <Route path="users" element={<UserAdmin authToken={authToken} />} />
        <Route
          path="chat-stats"
          element={<ChatStats authToken={authToken} />}
        />
        <Route
          path="monitoring/*"
          element={<MonitoringDashboard authToken={authToken} />}
        />
        <Route path="bot-list" element={<BotList authToken={authToken} />} />
        <Route path="bots" element={<BotAdmin authToken={authToken} />} />
        <Route
          path="twitch-apps"
          element={<TwitchApps authToken={authToken} />}
        />
        <Route
          path="twitch-chat"
          element={<TwitchChat authToken={authToken} />}
        />
        <Route path="email" element={<EmailAdmin authToken={authToken} />} />
      </Routes>
    </div>
  );
};

export default React.memo(Admin);
